import { 
    gql, 
    useQuery 
} from "@apollo/client";

import { 
    FindAllCustomersByLocationIdAdminQueryQuery,
    QueryFindAllCustomersByLocationIdAdminArgs,
} from "../../../../../schemaTypes";
import { useCallback } from "react";

export const findAllCustomersByLocationIdAdminQuery = gql`
  query FindAllCustomersByLocationIdAdminQuery(
    $locationId: String!
    $page: Int!
    $pageSize: Int!
    $searchText: String
    $duration: String
    $sortBy: String
    $sortOrder: String
  ) {
    findAllCustomersByLocationIdAdmin(
      locationId: $locationId
      page: $page
      pageSize: $pageSize
      searchText: $searchText
      duration: $duration
      sortBy: $sortBy
      sortOrder: $sortOrder
    ) {
      data {
        user{
            id
            firstname
            lastname
            username
        }
        community{
            id
            alias
            name
        }
        totalSpend
        location
      }
      total
      page
      pageSize
      totalPages
    }
  }
`;

export interface WithFindAllCustomersByLocationIdAdmin {
   data: FindAllCustomersByLocationIdAdminQueryQuery["findAllCustomersByLocationIdAdmin"]["data"]
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  loading: boolean;
  loadMore: () => void;
  hasMoreCustomers: boolean;
}

interface Props {
  variables: QueryFindAllCustomersByLocationIdAdminArgs;
  children: (data: WithFindAllCustomersByLocationIdAdmin) => React.ReactElement | null;
}

export const FindAllCustomersByLocationIdAdminController: React.FC<Props> = ({
  children,
  variables,
}) => {
  const { data, loading, fetchMore } = useQuery<
    FindAllCustomersByLocationIdAdminQueryQuery,
    QueryFindAllCustomersByLocationIdAdminArgs
   >(findAllCustomersByLocationIdAdminQuery, {
    variables,
    notifyOnNetworkStatusChange: true,
  });

  const findAllCustomersByLocationIdAdminData = data?.findAllCustomersByLocationIdAdmin?.data || [];
  const page = data?.findAllCustomersByLocationIdAdmin?.page || variables.page;
  const pageSize = data?.findAllCustomersByLocationIdAdmin?.pageSize || variables.pageSize;
  const total = data?.findAllCustomersByLocationIdAdmin?.total || 0;
  const totalPages = data?.findAllCustomersByLocationIdAdmin?.totalPages || 0;
  const hasMoreCustomers = page < totalPages;

  const loadMore = useCallback(() => {
    if (!hasMoreCustomers) return;
    fetchMore({
      variables: {
        ...variables,
        page: page + 1,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult || !fetchMoreResult?.findAllCustomersByLocationIdAdmin) {
          return prev;
        }
        return {
          ...fetchMoreResult,
          findAllCustomersByLocationIdAdmin: {
            ...fetchMoreResult?.findAllCustomersByLocationIdAdmin,
            data: [
              ...(prev?.findAllCustomersByLocationIdAdmin?.data || []),
              ...(fetchMoreResult?.findAllCustomersByLocationIdAdmin?.data || []),
            ],
          },
        };
      },
    });
  }, [fetchMore, variables, page, hasMoreCustomers]);

  return children({
    data: findAllCustomersByLocationIdAdminData as any,
    loading,
    loadMore,
    hasMoreCustomers,
    page,
    pageSize,
    total,
    totalPages,
  });
};